import { RECOVERY_REASON } from '../components/subscription-recovery/types/subscription-recovery-enums';
import { PAYMENT_GATEWAY, SUBSCRIPTION_STATUS } from '@adtrackify/at-tracking-event-types';
import { addDays } from 'date-fns';

import { useSubscriptionGuard } from '@/features/billing/hooks/use-subscription-guard';
import { useBillingPaymentMethods } from '@/features/billing/hooks/use-billing-payment-methods';
import { useAccountState } from '@/contexts/account/account-context';
import type { StripePaymentMethod } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';

// Only the default card is checked, and it counts as "expiring soon" when it stops
// working within the next 30 days.
const EXPIRING_SOON_WINDOW_DAYS = 30;

// Stripe cards remain valid through the end of `expMonth`, so the card actually becomes
// invalid at the start of the following month. Anchoring both checks on that instant
// keeps "expired" and "expiring soon" partitioned cleanly.
function getCardInvalidationDate(pm: StripePaymentMethod): Date | null {
  if (pm.type !== 'card' || !pm.card) return null;
  const { expMonth, expYear } = pm.card;
  return new Date(expYear, expMonth, 1);
}

function isCardExpired(pm: StripePaymentMethod): boolean {
  const invalidAt = getCardInvalidationDate(pm);
  if (!invalidAt) return false;
  return new Date() >= invalidAt;
}

function isCardExpiringSoon(pm: StripePaymentMethod): boolean {
  const invalidAt = getCardInvalidationDate(pm);
  if (!invalidAt) return false;
  const now = new Date();
  // invalidAt is the first day the card no longer works (start of the month after the
  // printed expiry). Example: on Oct 20, a 10/26 card stops working Nov 1 (12 days → soon);
  // an 11/26 card stops working Dec 1 (42 days → not yet).
  const threshold = addDays(now, EXPIRING_SOON_WINDOW_DAYS);
  return invalidAt > now && invalidAt <= threshold;
}

export function useSubscriptionRecoveryReason(): RECOVERY_REASON | null {
  const { isInRecovery, isCancelled, isLoading: isGuardLoading, subscriptionStatus } = useSubscriptionGuard();
  const { currentAccount } = useAccountState();

  const isStripe = currentAccount?.subscription?.paymentGateway === PAYMENT_GATEWAY.STRIPE;
  // Subscribe to the PM cache (populated by PaymentMethodsBootstrap) without
  // firing a fetch. We re-render whenever bootstrap resolves so the banner
  // appears mid-navigation. When the cache is undefined the bootstrap hasn't
  // resolved yet — fall through to subscription-status reasons or stay silent.
  const { data: paymentMethods } = useBillingPaymentMethods({ enabled: false });

  if (isGuardLoading || !isStripe) return null;

  // PENDING subs are gated (BILLING_ONLY) but still need the banner on exempted routes
  if (subscriptionStatus === SUBSCRIPTION_STATUS.PENDING) {
    return RECOVERY_REASON.PENDING;
  }

  if (isCancelled) return null;

  // Cardless trial users intentionally have no payment method — don't flag them.
  const subscription = currentAccount?.subscription;
  const isActiveCardlessTrial =
    subscription?.isCardlessTrial === true &&
    (subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE ||
      subscriptionStatus === SUBSCRIPTION_STATUS.TRIALING);

  // Subscription-level signals take priority — we know the user needs to act regardless
  // of PM state, and these fire faster than the PM query.
  let reason: RECOVERY_REASON | null = null;
  if (subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE) {
    reason = RECOVERY_REASON.PAST_DUE;
  } else if (subscriptionStatus === SUBSCRIPTION_STATUS.UNPAID) {
    reason = RECOVERY_REASON.UNPAID;
  } else if (paymentMethods && !isActiveCardlessTrial) {
    const defaultPm = paymentMethods.find(pm => pm.isDefault);
    if (paymentMethods.length === 0 || !defaultPm) {
      reason = RECOVERY_REASON.NO_PAYMENT_METHOD;
    } else if (isCardExpired(defaultPm)) {
      reason = RECOVERY_REASON.CARD_EXPIRED;
    } else if (isCardExpiringSoon(defaultPm)) {
      reason = RECOVERY_REASON.CARD_EXPIRING_SOON;
    }
  }
  // Defensive: cover any future recoverable status the hook flags but we haven't mapped
  // explicitly. Falling back to past-due copy is closer to the truth than hiding the
  // banner entirely.
  if (!reason && isInRecovery) {
    reason = RECOVERY_REASON.PAST_DUE;
  }

  return reason;
}
